import { Link } from '@/components/LocalizedLink';
import { useLanguage } from '@/contexts/LanguageContext';
import { getToolById } from '@/data/tools';
import { getLocalizedTool } from '@/data/toolsLocale';
import { LANDING_COPY, getLandingBandRows, type LandingContent, type LandingLang } from '@/lib/landingContent';
import { getPillarGuidesBySlugs, localizePillarGuideText } from '@/lib/pillarGuideCatalog';
import { getPortfolioProjectSlug, type PortfolioProjectKey } from '@/lib/portfolioProjects';

const linkClass = 'text-foreground underline underline-offset-4 hover:text-muted-foreground transition-colors';
const cardClass = 'rounded-2xl border border-border/70 p-6 lg:p-8 bg-card/40';

type BandsProps = {
  lang: LandingLang;
  landing: LandingContent;
  className?: string;
};

export function ExampleCostBandsBlock({ lang, landing, className = '' }: BandsProps) {
  const rows = getLandingBandRows(landing.costBands, lang);
  if (!rows.length) return null;
  return (
    <section data-testid="example-cost-bands" className={`${cardClass} ${className}`}>
      <h2 className="font-display text-2xl lg:text-3xl text-foreground mb-3">{LANDING_COPY.bandsTitle[lang]}</h2>
      <p className="font-body text-xs tracking-[0.15em] uppercase text-foreground/80 mb-3">{LANDING_COPY.bandsLabel[lang]}</p>
      <p className="font-body text-sm text-muted-foreground leading-relaxed max-w-3xl mb-6">{LANDING_COPY.bandsIntro[lang]}</p>
      <div className="overflow-x-auto">
        <table className="w-full text-left border border-border/60 rounded-xl overflow-hidden">
          <thead className="bg-background/60">
            <tr>
              {[LANDING_COPY.profileColumn, LANDING_COPY.scopeColumn, LANDING_COPY.priceColumn, LANDING_COPY.durationColumn].map((col) => (
                <th key={col.en} scope="col" className="font-body text-xs uppercase tracking-wider p-3 text-muted-foreground">
                  {col[lang]}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={`${row.profileLabel}-${row.scopeLabel}`} className="border-t border-border/50">
                <td className="font-body text-sm p-3 text-foreground">{row.profileLabel}</td>
                <td className="font-body text-sm p-3 text-muted-foreground">{row.scopeLabel}</td>
                <td className="font-body text-sm p-3 text-foreground whitespace-nowrap">{row.range}</td>
                <td className="font-body text-sm p-3 text-muted-foreground">{row.duration}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {landing.costBandNote ? (
        <p className="font-body text-sm text-muted-foreground leading-relaxed mt-4 max-w-3xl">{landing.costBandNote[lang]}</p>
      ) : null}
      <p className="font-body text-sm mt-4">
        <Link to="/tools/project-cost-estimator" className={linkClass}>
          {LANDING_COPY.estimatorLink[lang]}
        </Link>
      </p>
    </section>
  );
}

export function LandingSectionsBlock({ lang, landing, className = '' }: BandsProps) {
  if (!landing.sections?.length) return null;
  return (
    <div className={`grid grid-cols-1 lg:grid-cols-2 gap-6 ${className}`}>
      {landing.sections.map((section) => (
        <section key={section.title.en} className="rounded-2xl border border-border/70 p-6 bg-card/40">
          <h2 className="font-display text-2xl text-foreground mb-3">{section.title[lang]}</h2>
          <ul className="space-y-2">
            {section.items.map((item) => (
              <li key={item.en} className="font-body text-sm text-muted-foreground leading-relaxed">• {item[lang]}</li>
            ))}
          </ul>
        </section>
      ))}
    </div>
  );
}

export function CaseStudyLinksBlock({ landing, className = '' }: { landing: LandingContent; className?: string }) {
  const { lang, t } = useLanguage();
  const keys: PortfolioProjectKey[] = landing.caseStudies;
  if (!keys.length) return null;
  return (
    <section data-testid="landing-case-studies" className={`mt-16 border-t border-border pt-12 ${className}`}>
      <h2 className="font-display text-3xl font-bold text-foreground mb-3">{LANDING_COPY.caseStudiesTitle[lang]}</h2>
      <p className="font-body text-sm text-muted-foreground mb-6 max-w-3xl">
        {(landing.caseStudyNote ?? LANDING_COPY.caseStudiesNote)[lang]}
      </p>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {keys.map((key) => (
          <Link
            key={key}
            to={`/projects/${getPortfolioProjectSlug(key)}`}
            className="rounded-xl border border-border/70 p-5 bg-card/40 hover:bg-card transition-colors"
          >
            <h3 className="font-display text-lg text-foreground mb-2">{t(`projects.${key}.title`)}</h3>
            <p className="font-body text-sm text-muted-foreground leading-relaxed">{t(`projects.${key}.desc`)}</p>
          </Link>
        ))}
      </div>
    </section>
  );
}

export function ToolLinksBlock({ lang, toolIds, className = '' }: { lang: LandingLang; toolIds: string[]; className?: string }) {
  const tools = toolIds.flatMap((id) => {
    const tool = getToolById(id);
    return tool ? [getLocalizedTool(tool, lang)] : [];
  });
  if (!tools.length) return null;
  return (
    <section data-testid="landing-tools" className={`mt-16 border-t border-border pt-12 ${className}`}>
      <h2 className="font-display text-3xl font-bold text-foreground mb-6">{LANDING_COPY.toolsTitle[lang]}</h2>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {tools.map((tool) => (
          <Link
            key={tool.id}
            to={`/tools/${tool.id}`}
            className="rounded-xl border border-border/70 p-5 bg-card/40 hover:bg-card transition-colors"
          >
            <h3 className="font-display text-lg text-foreground mb-2">{tool.title}</h3>
            <p className="font-body text-sm text-muted-foreground leading-relaxed">{tool.tagline}</p>
          </Link>
        ))}
      </div>
    </section>
  );
}

export function GuideLinksBlock({ lang, slugs, className = '' }: { lang: LandingLang; slugs: string[]; className?: string }) {
  const guides = getPillarGuidesBySlugs(slugs);
  if (!guides.length) return null;
  return (
    <section className={`mt-16 border-t border-border pt-12 ${className}`}>
      <h2 className="font-display text-3xl font-bold text-foreground mb-6">{LANDING_COPY.guidesTitle[lang]}</h2>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {guides.map((guide) => (
          <Link
            key={guide.slug}
            to={`/guides/${guide.slug}`}
            className="rounded-xl border border-border/70 p-5 bg-card/40 hover:bg-card transition-colors"
          >
            <h3 className="font-display text-lg text-foreground mb-2">{localizePillarGuideText(guide.title, lang)}</h3>
            <p className="font-body text-sm text-muted-foreground leading-relaxed">{localizePillarGuideText(guide.seoDescription, lang)}</p>
          </Link>
        ))}
      </div>
    </section>
  );
}
